/**
 * Auth Context
 *
 * Provides authentication state and methods throughout the app
 * Uses React 19 patterns
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import type { PublicUser, LoginCredentials } from '@/domain/user/types'
import * as authService from './auth.service'
import * as twoFactorService from './two-factor.service'
import { tokenStorage, getErrorMessage } from '@/lib/api'
import { queryClient } from '@/lib/queryClient'
import { APP_ROUTES } from '@/config/constants'
import { toast } from '@/lib/toast'

// =============================================================================
// Types
// =============================================================================

interface Pending2FAState {
  userId: string
  email: string
}

interface AuthState {
  user: PublicUser | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
  // 2FA login states
  pending2FA: Pending2FAState | null      // Waiting for 2FA code
  requires2FASetup: boolean               // User role requires 2FA but not configured
  mustChangePassword: boolean             // User must change temporary password
}

interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => Promise<void>
  clearError: () => void
  // 2FA methods
  complete2FALogin: (code: string) => Promise<void>
  cancel2FALogin: () => void
  clearRequires2FASetup: () => void
  // Password change
  onPasswordChanged: () => void
}

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextValue | null>(null)

// =============================================================================
// Provider
// =============================================================================

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate()
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    error: null,
    pending2FA: null,
    requires2FASetup: false,
    mustChangePassword: false,
  })

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      const hasToken = tokenStorage.getAccessToken()

      if (!hasToken) {
        setState((prev) => ({ ...prev, isLoading: false }))
        return
      }

      try {
        const user = await authService.getCurrentUser()
        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          pending2FA: null,
          requires2FASetup: false,
          mustChangePassword: user.mustChangePassword ?? false,
        })
      } catch {
        tokenStorage.clearTokens()
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          pending2FA: null,
          requires2FASetup: false,
          mustChangePassword: false,
        })
      }
    }

    initAuth()
  }, [])

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null, pending2FA: null }))

      try {
        const response = await authService.login(credentials)

        // Check if 2FA verification is required
        if (response.requires2FA) {
          // User has 2FA enabled - wait for code verification
          setState((prev) => ({
            ...prev,
            isLoading: false,
            pending2FA: {
              userId: response.user.id,
              email: response.user.email,
            },
          }))
          return // Don't navigate - wait for 2FA code
        }

        // No 2FA required, complete login
        // CRITICAL: Clear React Query cache before setting new user to prevent data leakage
        queryClient.clear()

        const needsPasswordChange = response.user.mustChangePassword ?? false

        setState({
          user: response.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          pending2FA: null,
          requires2FASetup: response.requires2FASetup || false,
          mustChangePassword: needsPasswordChange,
        })

        toast.success('Sesión iniciada correctamente')
        // Navigation is handled declaratively by PublicRoute/ProtectedRoute based on state
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: getErrorMessage(error),
          pending2FA: null,
        }))
        throw error
      }
    },
    [navigate]
  )

  const logout = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }))

    try {
      await authService.logout()
    } catch {
      // Ignore logout errors, always clear local state
    } finally {
      // CRITICAL: Clear all React Query cache to prevent data leakage between users
      queryClient.clear()

      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        pending2FA: null,
        requires2FASetup: false,
        mustChangePassword: false,
      })
      toast.success('Sesión cerrada')
      navigate(APP_ROUTES.LOGIN)
    }
  }, [navigate])

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }))
  }, [])

  // Complete 2FA login after verifying code
  const complete2FALogin = useCallback(
    async (code: string) => {
      if (!state.pending2FA) {
        throw new Error('No pending 2FA login')
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        const response = await twoFactorService.complete2FALogin(
          state.pending2FA.userId,
          code
        )

        // Tokens are stored by the service
        tokenStorage.setTokens(response.accessToken, response.refreshToken)

        // CRITICAL: Clear React Query cache before setting new user to prevent data leakage
        queryClient.clear()

        const needsPasswordChange = (response.user as PublicUser).mustChangePassword ?? false

        setState({
          user: response.user as PublicUser,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          pending2FA: null,
          requires2FASetup: false,
          mustChangePassword: needsPasswordChange,
        })

        toast.success('Verificación 2FA exitosa')
        // Navigation is handled declaratively by PublicRoute/ProtectedRoute based on state
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: getErrorMessage(error),
        }))
        throw error
      }
    },
    [state.pending2FA, navigate]
  )

  // Cancel pending 2FA login
  const cancel2FALogin = useCallback(() => {
    setState((prev) => ({
      ...prev,
      pending2FA: null,
      error: null,
    }))
  }, [])

  // Clear requires2FASetup flag (after user sets up 2FA)
  const clearRequires2FASetup = useCallback(() => {
    setState((prev) => ({ ...prev, requires2FASetup: false }))
  }, [])

  // Called after a successful forced password change
  const onPasswordChanged = useCallback(() => {
    setState((prev) => ({
      ...prev,
      mustChangePassword: false,
      user: prev.user ? { ...prev.user, mustChangePassword: false } : null,
    }))
    navigate(APP_ROUTES.DASHBOARD)
  }, [navigate])

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    clearError,
    complete2FALogin,
    cancel2FALogin,
    clearRequires2FASetup,
    onPasswordChanged,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// =============================================================================
// Hook
// =============================================================================

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * ¿El usuario tiene este permiso?
 *
 * Existe para que las pantallas no deduzcan por el ROL lo que un rol puede
 * hacer. Deducirlo es lo que llevó a que el visor ofreciera medir y anotar a
 * todo el mundo cuando `annotation:create` es exclusivo del radiólogo: la
 * lista de permisos vive en el seed del servidor y la interfaz la repetía mal.
 *
 * Ante la duda, cierra: si la sesión aún no trae permisos, responde que no.
 * Peor que ocultar un botón es ofrecerlo y que el servidor lo niegue.
 *
 * NO es un control de seguridad. El servidor decide en cada ruta con
 * `requirePermission`; esto solo evita ofrecer lo que se va a negar.
 */
/**
 * ¿Está habilitado este módulo para la institución del usuario?
 *
 * Sirve para no ofrecer lo que no se compró: una IPS que no contrató la
 * agenda no debe ver "Citas" en el menú. NO es un control de acceso: el
 * servidor niega las rutas del módulo por su cuenta (requireModulo).
 *
 * El valor por defecto NO se decide aquí. El servidor envía el estado ya
 * resuelto de todos los módulos del catálogo, con su valor por defecto
 * aplicado (ver backend: domain/institution/modulos). Repetir esa regla en el
 * cliente crearía dos fuentes que un día discrepan, y la que manda es la del
 * servidor.
 *
 * Un módulo que no llegue se toma por apagado. Es lo prudente: el único caso
 * en que falta es una sesión antigua, y hay módulos que al encenderse amplían
 * quién lee historia clínica.
 */
export function useModulo(codigo: string): boolean {
  const { user } = useAuth()
  return user?.modulos?.[codigo] === true
}

export function usePermiso(codigo: string): boolean {
  const { user } = useAuth()
  return user?.permissions?.includes(codigo) ?? false
}
